/**
 * Spinner — a gentle rotating ring that inherits `currentColor`. Use inside
 * buttons (loading state) or standalone for inline / block loading. Pure CSS
 * animation; respects prefers-reduced-motion.
 */
import { forwardRef, type CSSProperties, type HTMLAttributes } from "react";

export type SpinnerSize = "sm" | "md" | "lg";

export interface SpinnerProps extends HTMLAttributes<HTMLSpanElement> {
  /** Visual size. @default "md" */
  readonly size?: SpinnerSize;
}

const DIMENSIONS: Record<SpinnerSize, { box: number; border: number }> = {
  sm: { box: 14, border: 2 },
  md: { box: 18, border: 2 },
  lg: { box: 26, border: 3 },
};

export const Spinner = forwardRef<HTMLSpanElement, SpinnerProps>(function Spinner(
  { size = "md", className, style, ...rest },
  ref,
) {
  const dim = DIMENSIONS[size];
  const sizeStyle: CSSProperties = {
    width: dim.box,
    height: dim.box,
    borderWidth: dim.border,
  };
  return (
    <span
      ref={ref}
      role="status"
      aria-label="Loading"
      className={["lc-spinner", className].filter(Boolean).join(" ")}
      style={{ ...sizeStyle, ...style }}
      {...rest}
    />
  );
});
